import React, { useEffect, useRef, useState } from 'react';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';

import { useUIColors } from '../../theme';
import { useUIText } from '../../i18n';
import { cx, type Styleable } from '../../variants';

export interface CopyableValueProps extends Styleable {
    /** The full value copied to the clipboard. */
    value: string;
    /**
     * What renders in place of `value` — defaults to `value` itself. Pass a
     * truncated string (e.g. `${value.slice(0, 8)}…`) for a long id where only
     * the copy needs the full value; the untruncated `value` is always what
     * gets copied and always what the native `title` tooltip shows.
     */
    display?: React.ReactNode;
    /** Accessible name for the value itself, read before "Copy"/"Copied". */
    ariaLabel?: string;
}

/**
 * EAA-1817: this exact affordance (mono value + copy button that swaps to a
 * checkmark for ~1.5s) was hand-rolled twice, independently, at the app level
 * — `apps/portal/src/pages/profile/CopyableValue.tsx` (untruncated, profile
 * claims) and `.../console/audit/CopyableId.tsx` (8-char-truncated, audit
 * actor column) — found while building the app-detail Connection tab's
 * Client ID/Issuer copy links, which would have been a THIRD copy. `display`
 * covers both existing shapes (omit it for the untruncated case, pass a
 * truncated string for the audit case) rather than forking two components.
 *
 * Fails silently on a rejected `navigator.clipboard.writeText` (insecure
 * context, denied permission, unsupported browser) — the value stays visible
 * and select-all-able either way, so there is nothing more useful to surface.
 */
export function CopyableValue({
    value,
    display,
    ariaLabel,
    className,
    style,
}: CopyableValueProps): React.ReactElement {
    const colors = useUIColors();
    const text = useUIText();
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef<number | undefined>(undefined);

    useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // See doc comment above — no user-facing error, value stays visible.
        }
    }

    const actionLabel = copied ? text.common.copied : text.common.copy;

    return (
        <span
            className={cx(className)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...style }}
        >
            <code
                title={value}
                style={{
                    fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                    fontSize: 13,
                    color: colors.text,
                }}
            >
                {display ?? value}
            </code>
            <button
                type="button"
                onClick={() => void handleCopy()}
                aria-label={ariaLabel ? `${actionLabel} ${ariaLabel}` : actionLabel}
                title={actionLabel}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 2,
                    border: 'none',
                    background: 'none',
                    color: copied ? colors.success : colors.textMuted,
                    cursor: 'pointer',
                    fontSize: 12,
                    lineHeight: 1,
                }}
            >
                {copied ? <CheckOutlined /> : <CopyOutlined />}
            </button>
        </span>
    );
}

export default CopyableValue;
